import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useStyle } from "../context/StyleContext";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import ImageTemplate from "./ImageTemplate";

const SharingOptions = () => {
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [photoURLs, setPhotoURLs] = useState([]);
  const [printTheme, setPrintTheme] = useState("light");
  const [qrCodeURL, setQrCodeURL] = useState("");
  const [loading, setLoading] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState("");
  const [compressionError, setCompressionError] = useState("");
  const navigate = useNavigate();
  const styles = useStyle();
  const combinedImagesRef = useRef(null);
  const printTemplateRef = useRef(null);

  useEffect(() => {
    const storedFilteredPhotos = JSON.parse(
      localStorage.getItem("filteredPhotos")
    );
    const storedPrintTheme = localStorage.getItem("printTheme") || "light";

    if (storedFilteredPhotos) {
      setFilteredPhotos(storedFilteredPhotos);
      setPrintTheme(storedPrintTheme);
      mockUploadPhotos(storedFilteredPhotos.map((photo) => photo.src));
    } else {
      navigate("/photo-capture");
    }
  }, [navigate]);

  const convertToDataURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => {
        reject(new Error("Failed to convert image to Data URL"));
      };
      img.src = url;
    });
  };

  const mockUploadPhotos = async (photos) => {
    try {
      const urls = await Promise.all(
        photos.map((photo) => convertToDataURL(photo))
      );
      setPhotoURLs(urls);
    } catch (error) {
      console.error("Error converting photos to Data URLs:", error);
    }
  };

  const uploadImageToBackend = async (blob) => {
    try {
      const formData = new FormData();

      // Append the image blob to formData with the field name 'image' and filename 'image.jpeg'
      formData.append("image", blob, "image.jpeg"); // Changed to .jpeg

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Server responded with ${res.status}: ${errorText}`);
      }

      const result = await res.json();
      return result.url;
    } catch (error) {
      console.error("Error uploading image to backend:", error);
      return null;
    }
  };

  const compressImage = async (canvas, maxSizeMB = 10) => {
    // Updated maxSizeMB to 10
    let quality = 0.95; // Start with higher quality since 10MB allows for better quality
    let blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );

    while (blob.size / 1024 / 1024 > maxSizeMB && quality > 0.1) {
      quality -= 0.05; // Reduce quality in smaller increments for better control
      blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );
    }

    if (blob.size / 1024 / 1024 > maxSizeMB) {
      throw new Error(`Unable to compress image below ${maxSizeMB}MB`);
    }

    return blob;
  };

  const generateQRCode = async () => {
    setLoading(true);
    setCompressionError("");
    const element = printTemplateRef.current;

    if (element) {
      try {
        const canvas = await html2canvas(element, {
          useCORS: true,
          logging: true,
          scale: 2, // Increased scale for better quality
          backgroundColor: "#000000",
          width: element.scrollWidth,
          height: element.scrollHeight,
        });

        // Compress the image to ensure it's under 10MB
        const compressedBlob = await compressImage(canvas, 10); // Set maxSizeMB to 10

        // Create a data URL for downloading the image (optional)
        const blobUrl = URL.createObjectURL(compressedBlob);

        // Now upload the image to the backend
        const downloadURL = await uploadImageToBackend(compressedBlob);

        if (downloadURL) {
          setQrCodeURL(downloadURL);
          QRCode.toDataURL(
            downloadURL,
            { errorCorrectionLevel: "H" },
            (err, url) => {
              if (err) {
                console.error("Error generating QR code:", err);
                return;
              }
              setQrCodeImage(url);
            }
          );
        }
        setLoading(false);
      } catch (error) {
        console.error("Error generating QR code:", error);
        setCompressionError(
          "Failed to compress image below 10MB. Please try again."
        );
        setLoading(false);
      }
    } else {
      console.error("Element to capture is not found");
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      <motion.h1
        className="text-6xl font-bold text-white mb-8"
        initial={{ opacity: 0, y: -50 }}
        style={{
          fontFamily: styles.fontFamily,
          color: styles.textColor,
          fontWeight: "900",
        }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          <ImageTemplate
            ref={printTemplateRef}
            photos={photoURLs}
            filter="vintage" // or use other filter based on your requirements
            theme={printTheme}
          />
        </div>
        SHARING OPTIONS
      </motion.h1>
      <div className="flex flex-col items-center justify-center mb-8">
        <motion.div
          className="flex flex-wrap justify-center relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          ref={combinedImagesRef}
        >
          {filteredPhotos.map((photo, index) => (
            <div
              key={index}
              className="polaroid flex flex-col justify-center items-center mx-4 my-4"
            >
              <img
                src={photo.src}
                alt={`Captured ${index + 1}`}
                className="rounded-lg shadow-lg border-solid border-8"
                style={{
                  width: "60%",
                  borderRadius: "10px",
                  borderColor: styles.borderColor,
                }}
              />
            </div>
          ))}
          {/* Add restaurant logo */}
          {/* <img
            src="./final_logo.png"
            alt="Restaurant Logo"
            className="absolute -top-[500px]  m-auto w-[400px] h-[400px]"
          /> */}
        </motion.div>
      </div>

      {!qrCodeURL && !loading && (
        <motion.div
          className="mt-4 flex justify-items-center flex-col gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.button
            onClick={generateQRCode}
            className="px-6 py-3 my-5 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            style={{
              backgroundColor: styles.backgroundColor,
              fontFamily: styles.fontFamily,
            }}
          >
            <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56"></span>
            <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700"></span>
            <span
              className="relative text-5xl p-5"
              style={{
                fontFamily: styles.fontFamily,
                fontWeight: "bold",
                color: styles.textColor,
              }}
            >
              Generate QR Code
            </span>
          </motion.button>
        </motion.div>
      )}

      {loading && (
        <div
          className="text-3xl text-white"
          style={{ fontFamily: styles.fontFamily }}
        >
          Generating QR Code...
        </div>
      )}

      {qrCodeImage && (
        <motion.div
          className="mt-4 flex justify-items-center flex-col gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <img
            src={qrCodeImage}
            alt="QR Code"
            className="shadow-lg m-auto scale-100 my-8"
          />
          <p
            className="text-white mt-4 text-3xl"
            style={{ fontFamily: styles.fontFamily, color: styles.textColor }}
          >
            Scan the QR code to download the digital copy
          </p>
          <motion.button
            onClick={() => navigate("/thank-you")}
            className="px-6 py-3 my-5 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition duration-300 ease-in-out relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-medium tracking-tighter rounded-lg group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            style={{
              backgroundColor: styles.backgroundColor,
              fontFamily: styles.fontFamily,
            }}
          >
            <span className="absolute w-0 h-0 transition-all duration-500 ease-out bg-green-500 rounded-full group-hover:w-56 group-hover:h-56"></span>
            <span className="absolute inset-0 w-full h-full -mt-1 rounded-lg opacity-30 bg-gradient-to-b from-transparent via-transparent to-gray-700"></span>
            <span
              className="relative text-5xl p-5"
              style={{
                fontFamily: styles.fontFamily,
                fontWeight: "bold",
                color: styles.textColor,
              }}
            >
              Finish
            </span>
          </motion.button>
        </motion.div>
      )}

      {compressionError && (
        <div
          className="text-red-500 mt-4"
          style={{ fontFamily: styles.fontFamily }}
        >
          {compressionError}
        </div>
      )}
    </div>
  );
};

export default SharingOptions;
